import React, { useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Trash2 } from "lucide-react";

/**
 * SwipeableRow — Apple/iOS-like swipe-to-delete para listas mobile.
 * - Apenas mobile: em desktop renderiza children direto sem overhead
 * - Swipe da direita para esquerda revela botão Excluir vermelho
 * - Tap no children passa
 *
 * Uso:
 *   <SwipeableRow onDelete={() => handleDelete(id)} enabled={isMobile}>
 *     <RowContent />
 *   </SwipeableRow>
 */
export default function SwipeableRow({ children, onDelete, enabled = true, threshold = 80, "data-testid": testid }) {
  const x = useMotionValue(0);
  const [revealed, setRevealed] = useState(false);
  // Opacidade do botão Excluir aumenta conforme swipe
  const deleteOpacity = useTransform(x, [-threshold, -threshold / 2, 0], [1, 0.6, 0]);
  const deleteScale = useTransform(x, [-threshold, -threshold / 2, 0], [1, 0.92, 0.85]);

  if (!enabled) {
    return <div data-testid={testid}>{children}</div>;
  }

  const handleDragEnd = (_e, info) => {
    if (info.offset.x < -threshold) {
      // Snap aberto
      x.set(-threshold);
      setRevealed(true);
    } else {
      // Fecha
      x.set(0);
      setRevealed(false);
    }
  };

  const handleDeleteClick = () => {
    if (revealed) {
      onDelete?.();
      // reset
      x.set(0);
      setRevealed(false);
    }
  };

  return (
    <div className="relative overflow-hidden" data-testid={testid}>
      {/* Botão Excluir (atrás) */}
      <motion.button
        type="button"
        style={{ opacity: deleteOpacity, scale: deleteScale }}
        onClick={handleDeleteClick}
        aria-label="Excluir"
        className="absolute inset-y-0 right-0 flex items-center justify-center w-20 bg-rose-500 text-white"
        data-testid="swipe-delete-btn"
      >
        <Trash2 size={18} />
      </motion.button>

      {/* Conteúdo arrastável */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -threshold, right: 0 }}
        dragElastic={{ left: 0.1, right: 0 }}
        style={{ x }}
        onDragEnd={handleDragEnd}
        className="relative bg-white"
      >
        {children}
      </motion.div>
    </div>
  );
}
